import { buildDialogHTML, getModalRoot } from './dom';
import { trapFocus } from './focus';
import type { ModalInstance, ModalOptions, ModalSize } from './types';

export type { ModalInstance, ModalOptions, ModalSize };

const stack: ModalInstance[] = [];

export function openModal(options: ModalOptions): ModalInstance {
	const {
		content,
		title,
		size = 'md',
		showClose = true,
		closeOnBackdrop = true,
		closeOnEsc = true,
		className,
		onOpen,
		onBeforeClose,
		onClose,
	} = options;

	const root = getModalRoot();
	const wrapper = buildDialogHTML({ title, showClose, size, className });
	const dialog = wrapper.querySelector('.ac-modal__dialog') as HTMLElement;
	const bodyEl = wrapper.querySelector('.ac-modal__body') as HTMLElement;

	setContent(bodyEl, content);

	root.appendChild(wrapper);
	document.body.style.overflow = 'hidden';

	let cleanupFocus: (() => void) | null = null;

	let open = true;

	const instance: ModalInstance = {
		el: dialog,
		bodyEl,
		close: () => close(),
		setTitle: (t: string) => {
			const titleEl = dialog.querySelector('.ac-modal__title');
			if (titleEl) titleEl.textContent = t;
		},
		setContent: (c: HTMLElement | string) => setContent(bodyEl, c),
		isOpen: () => open,
	};

	const close = () => {
		if (!open) return;
		if (onBeforeClose && onBeforeClose(instance) === false) return;

		open = false;
		wrapper.classList.remove('is-open');
		wrapper.classList.add('is-closing');

		const afterTransition = () => {
			wrapper.removeEventListener('transitionend', afterTransition);
			wrapper.remove();
			cleanupFocus?.();
			document.removeEventListener('keydown', handleEsc);

			const idx = stack.indexOf(instance);
			if (idx !== -1) stack.splice(idx, 1);

			if (stack.length === 0) {
				document.body.style.overflow = '';
			}

			onClose?.();
		};

		wrapper.addEventListener('transitionend', afterTransition);
		setTimeout(afterTransition, 300);
	};

	if (closeOnBackdrop) {
		wrapper
			.querySelector('.ac-modal__backdrop')!
			.addEventListener('click', close);
	}

	const closeBtn = dialog.querySelector('.ac-modal__close');
	if (closeBtn) {
		closeBtn.addEventListener('click', close);
	}

	dialog.addEventListener('click', (e) => {
		if ((e.target as HTMLElement).closest('[data-action="close"]')) {
			close();
		}
	});

	const handleEsc = (e: KeyboardEvent) => {
		if (
			e.key === 'Escape' &&
			closeOnEsc &&
			stack[stack.length - 1] === instance
		) {
			close();
		}
	};
	document.addEventListener('keydown', handleEsc);

	requestAnimationFrame(() => {
		wrapper.classList.add('is-open');
		cleanupFocus = trapFocus(dialog);
		onOpen?.(instance);
	});

	stack.push(instance);
	return instance;
}

export function closeModal(): void {
	const top = stack[stack.length - 1];
	top?.close();
}

export function closeAllModals(): void {
	[...stack].reverse().forEach((m) => m.close());
}

export function getActiveModal(): ModalInstance | null {
	return stack[stack.length - 1] ?? null;
}

export function confirmModal(options: {
	title?: string;
	message: string;
	confirmLabel?: string;
	cancelLabel?: string;
	confirmClass?: string;
	size?: ModalSize;
}): Promise<boolean> {
	return new Promise((resolve) => {
		const {
			title = 'Confirm',
			message,
			confirmLabel = 'Confirm',
			cancelLabel = 'Cancel',
			confirmClass = 'ac-btn--primary',
			size = 'sm',
		} = options;

		const body = document.createElement('div');
		body.innerHTML = `
			<p style="font-size:13px;color:var(--ac-text-muted);margin-bottom:var(--space-4)">${escapeHTML(message)}</p>
			<div style="display:flex;gap:8px;justify-content:flex-end">
				<button type="button" class="ac-btn ac-btn--ghost ac-btn--sm" data-action="cancel">${escapeHTML(cancelLabel)}</button>
				<button type="button" class="ac-btn ${confirmClass} ac-btn--sm" data-action="confirm">${escapeHTML(confirmLabel)}</button>
			</div>
		`;

		const modal = openModal({
			content: body,
			title,
			size,
			onClose: () => resolve(false),
		});

		body
			.querySelector('[data-action="cancel"]')!
			.addEventListener('click', () => {
				modal.close();
				resolve(false);
			});
		body
			.querySelector('[data-action="confirm"]')!
			.addEventListener('click', () => {
				modal.close();
				resolve(true);
			});
	});
}

export interface AllCoachModalGlobal {
	open: typeof openModal;
	close: typeof closeModal;
	closeAll: typeof closeAllModals;
	confirm: typeof confirmModal;
	getActive: typeof getActiveModal;
}

declare global {
	interface Window {
		allcoach: { modal: AllCoachModalGlobal } & Record<string, unknown>;
	}
}

window.allcoach = window.allcoach || ({} as Window['allcoach']);
window.allcoach.modal = {
	open: openModal,
	close: closeModal,
	closeAll: closeAllModals,
	confirm: confirmModal,
	getActive: getActiveModal,
};

function setContent(bodyEl: HTMLElement, content: HTMLElement | string): void {
	bodyEl.innerHTML = '';
	if (typeof content === 'string') {
		if (content.startsWith('#') || content.startsWith('.')) {
			const el = document.querySelector(content);
			if (el) {
				bodyEl.appendChild(el.cloneNode(true));
				return;
			}
		}
		bodyEl.innerHTML = content;
	} else {
		bodyEl.appendChild(content);
	}
}

function escapeHTML(str: string): string {
	const div = document.createElement('div');
	div.textContent = str;
	return div.innerHTML;
}
